import { useCallback, useState } from 'react'
import { DateRange } from 'react-day-picker'

import { getSpendLensSubCategories } from '@/api/clarityLens'
import { formatMetric } from '@/hooks/clarity/util'
import { IAbstractLensItem } from '@/types'
import { SpendLensCategoryModel } from '@/types/clarityLens'
import { dateToString } from '@/utils'
import { DateType, getTrendingDate } from '@/utils/date'

export default function useSpendLensSubCategories(
  startDate: string,
  endDate: string,
  dateFilterType: DateType,
  branches: string
) {
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)
  const [subCategoriesData, setSubCategoriesData] = useState<
    Map<string, SpendLensCategoryModel[]>
  >(new Map())
  const [trendSubCategoriesData, setTrendSubCategoriesData] = useState<
    Map<string, SpendLensCategoryModel[]>
  >(new Map())

  const fetchSubCategoriesForCategory = useCallback(
    async (category: string): Promise<SpendLensCategoryModel[]> => {
      // Return cached data if available
      if (subCategoriesData.has(category)) {
        return subCategoriesData.get(category)!
      }

      setLoading(true)
      setError(null)

      try {
        const [currentData, trendData] = await Promise.all([
          fetchSubCategoriesData(branches, startDate, endDate, category),
          fetchTrendSubCategoriesData(branches, category),
        ])

        // Update the cache with new data
        setSubCategoriesData(
          (prev) => new Map(prev.set(category, currentData.data))
        )
        setTrendSubCategoriesData(
          (prev) => new Map(prev.set(category, trendData.data))
        )

        return currentData.data
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'An unknown error occurred'
        setError(new Error(errorMessage))
        throw new Error(errorMessage)
      } finally {
        setLoading(false)
      }
    },
    [branches, startDate, endDate, subCategoriesData]
  )

  const fetchTrendSubCategoriesData = useCallback(
    async (
      branches: string,
      category: string
    ): Promise<{ data: SpendLensCategoryModel[] }> => {
      const trendingDate = getTrendingDate(
        { from: new Date(startDate), to: new Date(endDate) },
        dateFilterType
      ) as DateRange
      return await fetchSubCategoriesData(
        branches,
        dateToString(trendingDate, 'from'),
        dateToString(trendingDate, 'to'),
        category
      )
    },
    []
  )

  const hasSubCategoriesForCategory = useCallback(
    (category: string): boolean => {
      return subCategoriesData.has(category)
    },
    [subCategoriesData]
  )

  const hasTrendSubCategoriesForCategory = useCallback(
    (category: string): boolean => {
      return trendSubCategoriesData.has(category)
    },
    [trendSubCategoriesData]
  )

  const getFormattedSubCategoriesForCategory = useCallback(
    (
      category: string
    ): Array<IAbstractLensItem<any> & { parentName?: string }> => {
      const items = subCategoriesData.get(category) || []
      const trendItems = trendSubCategoriesData.get(category) || []

      // Create a map of trend data by item name for easy lookup
      const trendDataMap = new Map(
        trendItems.map((item) => [item.category, item])
      )

      return items.map(
        (
          item: SpendLensCategoryModel
        ): IAbstractLensItem<any> & { parentName?: string } => {
          const trendItem = trendDataMap.get(item.category)

          return {
            item: item.category,
            parentName: category, // Use parentCategory from model
            current: {
              totalSpend: item.totalSpend,
              spendSharePercentage: item.spendSharePercentage,
              averageUnitPrice: item.averageUnitPrice,
              activeSkuCount: item.activeSkuCount,
            },
            prev: trendItem
              ? {
                  totalSpend: trendItem.totalSpend,
                  spendSharePercentage: trendItem.spendSharePercentage,
                  averageUnitPrice: trendItem.averageUnitPrice,
                  activeSkuCount: trendItem.activeSkuCount,
                }
              : undefined,
          }
        }
      )
    },
    [subCategoriesData, trendSubCategoriesData]
  )

  const clearCache = useCallback(() => {
    setSubCategoriesData(new Map())
    setTrendSubCategoriesData(new Map())
    setError(null)
  }, [])

  const refetch = useCallback(() => {
    console.log('Refetching spend lens sub categories (clearing cache)...')
    clearCache()
  }, [clearCache])

  return {
    fetchSubCategoriesForCategory,
    hasSubCategoriesForCategory,
    hasTrendSubCategoriesForCategory,
    getFormattedSubCategoriesForCategory,
    clearCache,
    refetch,
    loading,
    error,
  }
}

async function fetchSubCategoriesData(
  branches: string,
  startDate: string,
  endDate: string,
  category: string
): Promise<{
  data: SpendLensCategoryModel[]
}> {
  try {
    const response = await getSpendLensSubCategories(
      branches,
      startDate,
      endDate,
      category
    )

    const data: SpendLensCategoryModel[] = response.map((responseItem) => {
      return convertData(
        responseItem.category,
        responseItem.total_spend,
        responseItem.spend_share_percentage,
        responseItem.average_unit_price,
        responseItem.active_sku_count,
        category
      )
    })

    return {
      data,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

function convertData(
  category: string,
  totalSpend: number,
  spendSharePercentage: number,
  averageUnitPrice: number,
  activeSkuCount: number,
  parentCategory?: string
): SpendLensCategoryModel {
  return {
    category,
    parentCategory,
    totalSpend: formatMetric(totalSpend, 2),
    spendSharePercentage: formatMetric(spendSharePercentage, 2),
    averageUnitPrice: formatMetric(averageUnitPrice, 2),
    activeSkuCount: formatMetric(activeSkuCount, 2),
  }
}
